/**
 * 인게이지먼트(체류) 추적 — 하트비트 + 이탈 이벤트 + 유휴 컷오프
 *
 * GA4 의 user_engagement 와 유사한 개념. 페이지가 보이는 동안 주기적으로 `heartbeat`
 * 이벤트를 전송해 세션의 "마지막 활동 시각"을 갱신하고, 페이지가 숨겨지거나(visibilitychange
 * → hidden) 언로드될 때(pagehide) `page_exit` 이벤트를 전송한다.
 *
 * 이탈 이벤트는 브라우저 특성상 전송이 보장되지 않으므로(특히 모바일), 하트비트가 남긴
 * 마지막 활동 시각을 이탈 시각의 근사치로 사용한다(오차 ≤ 하트비트 주기).
 *
 * ▶ 유휴 컷오프(GA4 정합): 마지막 사용자 상호작용(마우스/키보드/스크롤/터치 등) 이후
 *   `idleTimeoutMs` 가 지나면 하트비트를 중단한다. 방치된(보이지만 조작 없는) 탭이
 *   몇 시간이고 세션을 연장하는 것을 막는다 — 하트비트가 멈추면 last_activity_at 이
 *   마지막 관여 시각에 고정되고, 세션 마감 배치(30분 유휴)가 정상적으로 세션을 종료한다.
 *   (GA4 도 백그라운드 시간은 세지 않고, 무활동 세션은 세션 타임아웃으로 종료된다.)
 */

import type { LambAnalytics } from '../core/sdk';

/** 기본 하트비트 주기(ms). 마지막 페이지 체류시간 오차의 상한이 된다. */
export const DEFAULT_HEARTBEAT_INTERVAL_MS = 15000;

/** 하트비트 최소 주기(ms). 과도한 요청/DB 부하를 막기 위한 하한. */
export const MIN_HEARTBEAT_INTERVAL_MS = 1000;

/** 기본 유휴 컷오프(ms). 마지막 상호작용 이후 이 시간이 지나면 하트비트를 멈춘다. */
export const DEFAULT_IDLE_TIMEOUT_MS = 5 * 60 * 1000;

/** 유휴 컷오프 최소값(ms). 너무 짧아 정상 체류가 끊기는 것을 막는 하한. */
export const MIN_IDLE_TIMEOUT_MS = 10000;

/** 사용자 관여(engagement) 신호로 볼 상호작용 이벤트. */
const INTERACTION_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
  'click',
] as const;

/**
 * 미디어 재생을 관여 신호로 볼 이벤트.
 *
 * 영상/음원을 손 대지 않고 시청만 하는 사용자(마우스·키보드 무조작)가 유휴 컷오프로
 * 끊기지 않게 한다 — GA4 도 visible 상태의 시청 시간을 관여로 누적한다.
 * 미디어 이벤트는 버블되지 않으므로 window 의 **capture 단계**로 수신해야 한다.
 * (timeupdate 는 재생 중 ~4Hz 로 발화 — 핸들러는 시각 대입 1회라 비용 무시 가능.)
 */
const MEDIA_ENGAGEMENT_EVENTS = ['play', 'timeupdate'] as const;

/** init 옵션의 시간값을 검증한다. 유효한 양수면 하한을 적용해 채택, 아니면 기본값. */
function resolveDuration(value: number | undefined, fallback: number, min: number): number {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.max(value, min);
  }
  return fallback;
}

/** EngagementTracker 튜닝 옵션(init 의 autoTrack.engagement 객체 형태와 동일). */
export interface EngagementOptions {
  /** 하트비트 발사 주기(ms). 기본 15000, 최소 1000. */
  heartbeatIntervalMs?: number;
  /** 유휴 컷오프(ms). 마지막 상호작용 후 이 시간이 지나면 하트비트 중단. 기본 300000, 최소 10000. */
  idleTimeoutMs?: number;
}

export class EngagementTracker {
  private sdk: LambAnalytics;
  private intervalMs: number;
  private idleTimeoutMs: number;
  private timer: ReturnType<typeof setInterval> | null = null;
  private active = false;
  /** 숨김/언로드가 동시에 발화할 때 page_exit 중복 전송을 방지한다. */
  private exited = false;
  /** 마지막 사용자 상호작용(또는 페이지 표시) 시각(epoch ms). 유휴 판정 기준. */
  private lastInteractionAt = 0;

  private visibilityHandler: (() => void) | null = null;
  private pageHideHandler: (() => void) | null = null;
  private interactionHandler: (() => void) | null = null;

  constructor(sdk: LambAnalytics, options: EngagementOptions = {}) {
    this.sdk = sdk;
    this.intervalMs = resolveDuration(
      options.heartbeatIntervalMs,
      DEFAULT_HEARTBEAT_INTERVAL_MS,
      MIN_HEARTBEAT_INTERVAL_MS,
    );
    this.idleTimeoutMs = resolveDuration(
      options.idleTimeoutMs,
      DEFAULT_IDLE_TIMEOUT_MS,
      MIN_IDLE_TIMEOUT_MS,
    );
  }

  start(): void {
    if (this.active || typeof window === 'undefined') return;
    this.active = true;
    this.lastInteractionAt = Date.now();

    this.visibilityHandler = (): void => this.onVisibilityChange();
    this.pageHideHandler = (): void => this.sendExit();
    this.interactionHandler = (): void => {
      this.lastInteractionAt = Date.now();
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
    window.addEventListener('pagehide', this.pageHideHandler);
    for (const evt of INTERACTION_EVENTS) {
      window.addEventListener(evt, this.interactionHandler, { passive: true });
    }
    // 미디어 이벤트는 버블되지 않음 → capture 단계로 문서 전체의 재생을 감지.
    for (const evt of MEDIA_ENGAGEMENT_EVENTS) {
      window.addEventListener(evt, this.interactionHandler, { capture: true, passive: true });
    }

    if (this.isVisible()) {
      this.startTimer();
    }
  }

  stop(): void {
    if (!this.active || typeof window === 'undefined') return;
    this.active = false;

    this.stopTimer();

    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = null;
    }
    if (this.interactionHandler) {
      for (const evt of INTERACTION_EVENTS) {
        window.removeEventListener(evt, this.interactionHandler);
      }
      // capture 로 등록한 리스너는 해제 시에도 capture 플래그가 일치해야 한다.
      for (const evt of MEDIA_ENGAGEMENT_EVENTS) {
        window.removeEventListener(evt, this.interactionHandler, { capture: true });
      }
      this.interactionHandler = null;
    }
  }

  private isVisible(): boolean {
    return typeof document === 'undefined' || document.visibilityState !== 'hidden';
  }

  /** 마지막 상호작용 이후 유휴 컷오프를 초과했는지. 초과 시 하트비트/이탈을 보내지 않는다. */
  private isIdle(): boolean {
    return Date.now() - this.lastInteractionAt > this.idleTimeoutMs;
  }

  private onVisibilityChange(): void {
    if (!this.isVisible()) {
      // 페이지 숨김 → 이탈로 간주(활동 없었다면 스킵) 후 하트비트 정지.
      this.sendExit();
      this.stopTimer();
    } else {
      // 다시 보임(탭 복귀) → 재관여로 보고 유휴 타이머를 리셋하고 하트비트 재개.
      this.exited = false;
      this.lastInteractionAt = Date.now();
      this.startTimer();
    }
  }

  private startTimer(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.sendHeartbeat(), this.intervalMs);
  }

  private stopTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private sendHeartbeat(): void {
    // 숨김(타이머는 이미 정지) 또는 유휴 상태면 활동 시각을 갱신하지 않는다.
    // → 방치된 탭은 last_activity_at 이 고정되어 세션 마감 배치로 정상 종료된다(GA4 정합).
    if (!this.isVisible() || this.isIdle()) return;
    this.sdk.track('heartbeat');
  }

  private sendExit(): void {
    if (this.exited) return;
    // 이미 유휴(사용자가 떠남)라면 이탈 이벤트로 활동 시각을 되살리지 않는다.
    // 하트비트가 멈춰 마지막 관여 시각이 확정돼 있으므로 세션은 그 시점 기준으로 종료된다.
    if (this.isIdle()) return;
    this.exited = true;
    this.sdk.track('page_exit');
    // 언로드 직전 확실한 전송을 위해 즉시 동기 flush(sendBeacon).
    this.sdk.flush();
  }
}
